import React from 'react';
import { Calendar, Clock, MapPin, Users, Star, ChevronRight } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';

const Dashboard: React.FC = () => {
  const { user } = useAuth();

  // Mock data - in real app, this would come from API
  const upcomingReviews = [
    {
      id: '1',
      eventTitle: 'Portfolio Review Session - Spring 2025',
      date: new Date('2025-08-20T14:00:00'),
      duration: 30,
      type: 'online' as const,
      reviewer: 'Sarah Johnson',
      status: 'scheduled' as const,
    },
    {
      id: '2',
      eventTitle: 'Resume Workshop',
      date: new Date('2025-08-25T10:00:00'),
      duration: 45,
      type: 'in-person' as const,
      location: 'Design Building, Room 101',
      reviewer: 'Mike Chen',
      status: 'scheduled' as const,
    },
  ];

  const pastReviews = [
    {
      id: '3',
      eventTitle: 'Portfolio Review Session - Summer 2025',
      date: new Date('2025-07-15T15:00:00'),
      reviewer: 'Emily Davis',
      rating: 5,
      feedback: 'Excellent portfolio with strong visual hierarchy. Great attention to detail.',
      status: 'completed' as const,
    },
  ];

  const availableEvents = [
    {
      id: '1',
      title: 'Fall Portfolio Reviews',
      description: 'Get feedback on your design portfolio from industry professionals',
      type: 'online' as const,
      startDate: new Date('2025-09-01'),
      endDate: new Date('2025-09-30'),
      availableSlots: 15,
    },
    {
      id: '2',
      title: 'UX Design Workshop',
      description: 'Interactive workshop on user experience design principles',
      type: 'in-person' as const,
      startDate: new Date('2025-09-15'),
      endDate: new Date('2025-09-15'),
      location: 'Innovation Lab',
      availableSlots: 8,
    },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Welcome Section */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">
          Welcome back, {user?.firstName}!
        </h1>
        <p className="text-gray-600 mt-2">
          Here's what's happening with your portfolio reviews
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Calendar className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Upcoming Reviews</p>
              <p className="text-2xl font-bold text-gray-900">{upcomingReviews.length}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <Star className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Completed Reviews</p>
              <p className="text-2xl font-bold text-gray-900">{pastReviews.length}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Users className="h-6 w-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Available Events</p>
              <p className="text-2xl font-bold text-gray-900">{availableEvents.length}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <Clock className="h-6 w-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Avg. Rating</p>
              <p className="text-2xl font-bold text-gray-900">
                {pastReviews.length > 0 
                  ? (pastReviews.reduce((acc, review) => acc + (review.rating || 0), 0) / pastReviews.length).toFixed(1)
                  : '--'
                }
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Upcoming Reviews */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Upcoming Reviews</h2>
          </div>
          <div className="p-6">
            {upcomingReviews.length > 0 ? (
              <div className="space-y-4">
                {upcomingReviews.map((review) => (
                  <div key={review.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h3 className="font-medium text-gray-900">{review.eventTitle}</h3>
                        <div className="mt-2 space-y-1">
                          <div className="flex items-center text-sm text-gray-600">
                            <Calendar className="h-4 w-4 mr-2" />
                            {review.date.toLocaleDateString()} at {review.date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </div>
                          <div className="flex items-center text-sm text-gray-600">
                            <Clock className="h-4 w-4 mr-2" />
                            {review.duration} minutes
                          </div>
                          {review.type === 'in-person' && review.location && (
                            <div className="flex items-center text-sm text-gray-600">
                              <MapPin className="h-4 w-4 mr-2" />
                              {review.location}
                            </div>
                          )}
                          <div className="flex items-center text-sm text-gray-600">
                            <Users className="h-4 w-4 mr-2" />
                            Reviewer: {review.reviewer}
                          </div>
                        </div>
                      </div>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        review.type === 'online' 
                          ? 'bg-blue-100 text-blue-800' 
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {review.type}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">No upcoming reviews scheduled</p>
            )}
          </div>
        </div>

        {/* Available Events */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Available Events</h2>
          </div>
          <div className="p-6">
            {availableEvents.length > 0 ? (
              <div className="space-y-4">
                {availableEvents.map((event) => (
                  <div key={event.id} className="border border-gray-200 rounded-lg p-4 hover:border-primary-300 transition-colors">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h3 className="font-medium text-gray-900">{event.title}</h3>
                        <p className="text-sm text-gray-600 mt-1">{event.description}</p>
                        <div className="mt-2 space-y-1">
                          <div className="flex items-center text-sm text-gray-600">
                            <Calendar className="h-4 w-4 mr-2" />
                            {event.startDate.toLocaleDateString()} - {event.endDate.toLocaleDateString()}
                          </div>
                          {event.location && (
                            <div className="flex items-center text-sm text-gray-600">
                              <MapPin className="h-4 w-4 mr-2" />
                              {event.location}
                            </div>
                          )}
                          <div className="flex items-center text-sm text-gray-600">
                            <Users className="h-4 w-4 mr-2" />
                            {event.availableSlots} slots available
                          </div>
                        </div>
                      </div>
                      <button className="ml-4 inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-primary-700 bg-primary-100 hover:bg-primary-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500">
                        Book Now
                        <ChevronRight className="ml-1 h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">No events available at the moment</p>
            )}
          </div>
        </div>
      </div>

      {/* Recent Reviews */}
      {pastReviews.length > 0 && (
        <div className="mt-8 bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Recent Reviews</h2>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {pastReviews.map((review) => (
                <div key={review.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-900">{review.eventTitle}</h3>
                      <div className="mt-2 space-y-1">
                        <div className="flex items-center text-sm text-gray-600">
                          <Calendar className="h-4 w-4 mr-2" />
                          {review.date.toLocaleDateString()}
                        </div>
                        <div className="flex items-center text-sm text-gray-600">
                          <Users className="h-4 w-4 mr-2" />
                          Reviewer: {review.reviewer}
                        </div>
                        {review.rating && (
                          <div className="flex items-center">
                            {[...Array(5)].map((_, i) => (
                              <Star
                                key={i}
                                className={`h-4 w-4 ${
                                  i < review.rating! ? 'text-yellow-400 fill-current' : 'text-gray-300'
                                }`}
                              />
                            ))}
                            <span className="ml-2 text-sm text-gray-600">({review.rating}/5)</span>
                          </div>
                        )}
                        {review.feedback && (
                          <p className="text-sm text-gray-700 mt-2 italic">"{review.feedback}"</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;